import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { SessionService } from "src/app/session.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FlowsService } from "../flows.service";
import { UiService } from "src/app/ui/ui.service";
import { PermissionService } from "src/app/permission.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import {
  BehaviorSubject,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { DataSource } from "@angular/cdk/table";
import { ElementLoaderDirective } from "src/app/ui/element-loader/element-loader.directive";
import { CollectionViewer } from "@angular/cdk/collections";
import moment from 'moment';
import { UtilService } from "src/app/util/util.service";
import { LocalStorageService } from "src/app/storage/local-storage.service";

enum ViewType {
  GRID = "grid",
  LIST = "list",
}

@Component({
  selector: "app-flows-library",
  templateUrl: "./flows-library.component.html",
  styleUrls: ["./flows-library.component.scss"],
})
export class FlowsLibraryComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  selectedView: ViewType = ViewType.LIST;
  searchValueControl = new FormControl("");
  perPage: number = 20;
  columns = [
    {
      name: "Flow name",
      code: "name",
      filter: true,
      selected: true,
    },
    {
      name: "Trigger",
      code: "trigger_name",
    },
    {
      name: "Created",
      code: "created_at",
      filter: true
    },
    {
      name: "Created by",
      code: "creator_name",
      filter: true
    },
    {
      name: "Last modified",
      code: "modified_at",
    },
    {
      name: "Modified by",
      code: "modified_by_name",
    },
    {
      name: "Conversions",
      code: "conversions",
    },
    {
      name: "",
      code: "options",
    },
  ];
  displayedColumns = this.columns.map((column) => column.code);
  sortColumns = this.columns.filter((column) => column.filter);
  sortFormGroup = new FormGroup({
    sort: new FormControl(""),
  });
  // flows!: any[];
  // filteredFlows!: any[];
  filterColumns!: { name: string; code: string }[];
  @ViewChild("flowTable") flowTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  hasDelete: boolean = false;
  hasWrite: boolean = false;
  hasReport: boolean = false;
  @ViewChild(ElementLoaderDirective)
  flowsElementLoader!: ElementLoaderDirective;

  private onComponentDestroy = new Subject();
  flowsDataSource!: FlowsDataSource;
  audienceContainer: any;
  flowsInitiated: boolean = false;
  flows: any;
  filteredFlows: any;
  flowsUpdated: boolean = false;
  currentTableChange$?: Subscription;
  onFilterColumnChange = new Subject();
  internal: boolean = false;
  hasFlowsWrite: boolean = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private FlowsService: FlowsService,
    private uiService: UiService,
    private PermissionService: PermissionService,
    private ChangeDetectorRef: ChangeDetectorRef,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {

    this.perPage = this.localStorageSvc.pageSize || 20;

    this.hasFlowsWrite = this.route.snapshot.data.hasFlowsWrite;

    this.sortFormGroup.controls.sort.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((sortBy) => {
        this.filteredFlows = this.filteredFlows.sort((a: any, b: any) => {
          let aValue = a[sortBy];
          let bValue = b[sortBy];

          if (aValue?.toLowerCase && bValue?.toLowerCase) {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }

          if (aValue < bValue) {
            return -1;
          }
          if (aValue > bValue) {
            return 1;
          }
          return 0;
        });
      });
    Promise.all([
      this.PermissionService.categories.logicTrees.hasDelete(),
      this.PermissionService.categories.logicTrees.hasWrite(),
      this.PermissionService.categories.logicTrees.hasReport(),
    ]).then(([hasDelete, hasWrite, hasReport]) => {
      this.hasDelete = hasDelete;
      this.hasWrite = hasWrite;
      this.hasReport = hasReport;
    });

    //Search value controls for grid view
    merge(
      this.searchValueControl.valueChanges.pipe(distinctUntilChanged()),
      this.onFilterColumnChange,
    )
      .pipe(
        filter(() => this.selectedView === ViewType.GRID),
        debounceTime(300),
        takeUntil(this.onComponentDestroy),
      )
      .subscribe(() => {
        this.applyFilter();
      });
  }

  ngAfterViewInit() {
    // this.flowTable?.renderRows();
    // this.flowDataSource.sort = this.sort;
    // this.flowDataSource.paginator = this.paginator;

    this.flowsDataSource = new FlowsDataSource(
      this.FlowsService,
      this.paginator,
      this.flowsElementLoader,
    );

    this.route.data.subscribe(data => {
      var isInternal = this.route.snapshot.params.type == 'internal';
      if(isInternal != this.internal){
        this.internal = isInternal;
        this.paginator.pageIndex = 0;
        this.loadFlowsPage().pipe(first()).subscribe();
      }
    })

    this.loadFlowsPage().pipe(first()).subscribe();

    this.currentTableChange$ = merge(
      this.sort.sortChange.pipe(tap(() => (this.paginator.pageIndex = 0))),
      this.paginator.page,
      this.onFilterColumnChange,
      this.searchValueControl.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => (this.paginator.pageIndex = 0)),
      ),
    )
      .pipe(
        //Only load when we're on
        filter(() => this.selectedView === ViewType.LIST),
        debounceTime(300),
        switchMap(() => this.loadFlowsPage().pipe(first())),
        takeUntil(this.onComponentDestroy),
      )
      .subscribe();

    this.ChangeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get masterAccountID() {
    return this.SessionService.currentAccountValue!.accountID;
  }

  get facilityIDs() {
    return this.SessionService.selectedFacilities!.map((facility) => {
      return facility.id;
    });
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  initGridFlows() {
    if (!this.flowsInitiated || this.flowsUpdated) {
      return this.uiService
        .waitForEvent(
          this.FlowsService.getLogicTrees(
            this.SessionService.currentAccountValue!.accountID,
            { page: 0, perPage: 999999999, facility_id: this.facilityIDs },
          ),
        )
        .subscribe((flows) => {
          this.flows = flows.records;
          this.applyFilter();
          this.flowsInitiated = true;
          this.flowsUpdated = false;
        });
    }

    this.applyFilter();
    return of();
  }

  loadFlowsPage() {
    const sort = this.buildSort();

    return this.flowsDataSource.loadFlows(
      this.SessionService.currentAccountValue!.accountID,
      {
        internal: this.internal,
        facility_id: this.facilityIDs,
        page: this.paginator.pageIndex,
        perPage: this.paginator.pageSize,
        keyword: this.searchValueControl.value ?? "",
        sort,
        forCount: false,
        filter_columns: this.columns
          .filter((column) => column.selected)
          .map((column) => column.code),
      },
    );
  }

  buildSort() {
    const sort = [];
    if (this.sort && this.sort.direction.length) {
      sort.push(`${this.sort.active}:${this.sort.direction}`);
    }
    return sort;
  }

  changeLayout(viewType: string) {
    if (this.currentTableChange$) {
      this.currentTableChange$.unsubscribe();
    }

    if (viewType === "grid") {
      this.selectedView = ViewType.GRID;
      this.initGridFlows();
    } else if (viewType === "list") {
      this.selectedView = ViewType.LIST;
      setTimeout(() => {
        this.ngAfterViewInit();
      });
    }
  }

  applyFilter() {
    const searchValue = this.searchValueControl.value.trim().toLowerCase();
    const selectedFilterColumns = this.columns
      .filter((column) => column.selected)
      .map((column) => column.code);
    this.filteredFlows = this.flows.filter((flow: any) => {
      for (let selectedColumn of selectedFilterColumns) {
        if (
          flow[selectedColumn]?.toString().toLowerCase().includes(searchValue)
        ) {
          return true;
        }
      }
      return false;
    });
  }

  toggleFilterDialog() {
    this.dialog
      .open(FilterTableDialogComponent, {
        data: {
          columns: this.columns,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;

          this.onFilterColumnChange.next();
        }
      });
  }

  createFlow() {
    this.router.navigate(["flows", "create", this.internal ? "internal" : "app"]);
  }

  editFlow(flow: any) {
    this.router.navigate(["flows", "edit", this.internal ? "internal" : "app", flow.id]);
  }

  deleteFlow(flow: any) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this flow ( ${flow.name} )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        if (res) {
          this.uiService
            .waitForEvent(
              this.FlowsService.deleteLogicTree(this.masterAccountID, flow.id),
            )
            .pipe(
              tap(
                () => {
                  this.NotificationService.openSuccessSnackBar("Flow deleted");

                  this.flowsUpdated = true;
                },
                () => {
                  this.NotificationService.openErrorSnackBar(
                    "Error deleting flow",
                  );
                },
              ),
              switchMap(() => this.loadFlowsPage()),
              takeUntil(this.onComponentDestroy),
            )
            .subscribe();
        }
      });
  }

  changeFlowDisabled(flow: any, disabled: boolean) {
    if (flow.disabled !== disabled) {
      flow.disabled = disabled;
      this.flowsElementLoader
        .waitForEvent(
          this.FlowsService.updateLogicTree(
            this.SessionService.currentAccountValue!.accountID,
            flow.id,
            flow,
          ).pipe(
            tap(
              () => {
                this.NotificationService.openSuccessSnackBar(
                  `Flow ${flow.disabled ? "paused" : "started"}`,
                );
                this.flowsUpdated = true;
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error updating flow",
                );
              },
            ),
            switchMap(() => this.loadFlowsPage()),
          ),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe();
    }
  }

  goToReport(flow: any) {
    this.router.navigate(["flows", "report", flow.id]);
  }

  rowExpired(expiration: any) {
    return moment(expiration).isBefore(moment());
  }

  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }

}

export class FlowsDataSource implements DataSource<any> {
  private flowsSubject = new BehaviorSubject<any[]>([]);

  constructor(
    private FlowService: FlowsService,
    private paginator: MatPaginator,
    private flowsElementLoader: ElementLoaderDirective,
  ) {}

  connect(collectionViewer: CollectionViewer): Observable<any[]> {
    return this.flowsSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer) {
    this.flowsSubject.complete();
  }

  loadFlows(masterAccountID: number, queryParams: any) {
    return this.flowsElementLoader
      .waitForEvent(
        this.FlowService.getLogicTrees(masterAccountID, queryParams).pipe(
          catchError(() => of({ count: 0, records: [] })),
        ),
      )
      .pipe(
        tap((filter: any) => {
          this.paginator.length = filter.count;
          this.flowsSubject.next(filter.records);
        }),
      );
  }
}
